import React, { useMemo, useState, useEffect } from "react";
import { ResponsiveContainer } from "recharts";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Label,
} from "recharts";
import Loader from "../assets/Loader.png";
import LoaderAnimation from "../assets/LoaderAnimation.png";
import "../App.css";

export default function EfficiencyBarChart({
  data = [],
  width = 700,
  height = 310,
  colors = {
    efficiency: "#e60a11",
    durabilityPercent: "#878786",
    priceIndex: "#878786",
  },
  onBarClick,
  loader,
  filter,
  error,
  iError,
  selectedMeasurement,
  selectedToggle,
}) {
  const filteredData = useMemo(() => {
    const fieldMap = {
      valveSystem: "valveSystem",
      gearwheelSet: "gearWheelSet",
      radialBearingLoad: "radialBearingLoad",
    };

    if (!filter || typeof filter !== "object") return data;

    const activeFilters = Object.entries(filter).filter(
      ([_, value]) => value !== "" && value !== null && value !== undefined
    );

    return data.filter((item) => {
      return activeFilters.every(([key, value]) => {
        const field = fieldMap[key] || key;
        if (field === "radialBearingLoad")
          return Number(item[field]) >= Number(value);
        return (
          String(item[field]).toLowerCase() === String(value).toLowerCase()
        );
      });
    });
  }, [data, filter]);

  const displayData = useMemo(() => {
    return [...filteredData]
      .map((item) => ({
        ...item,
        efficiency: Math.round((item.efficiency * 100).toFixed(2)),
        durabilityPercent: parseFloat(item.durabilityPercent),
      }))
      .sort((a, b) => b.efficiency - a.efficiency)
      .slice(0, 10);
  }, [filteredData]);

  const [selectedBar, setSelectedBar] = useState(null); // Track selected bar
  useEffect(() => {
    setSelectedBar(null);
  }, [selectedMeasurement, selectedToggle]);

  const handleBarClick = (data, index) => {
    setSelectedBar(index); // Update selected bar index
    onBarClick(data, index); // Log selected bar data
  };

  return loader ? (
    <div className="loader-container">
      <div className="dot-loader">
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
      </div>
    </div>
  ) : iError || error || displayData?.length === 0 ? (
    <div
      className="no-data-message"
      style={{
        textAlign: "center",
        justifyContent: "center",
        padding: "1rem",
        color: "#555",
        fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
      }}
    >
      No Data to show for selected filtering criteria
    </div>
  ) : (
    <ResponsiveContainer width={width} height={height}>
      <BarChart
        width={width}
        height={height}
        data={displayData}
        margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
      >
        {/* <text
        x={width / 2}
        y={10}
        textAnchor="middle"
        style={{ fontSize: "14px", fill: "#ac070c" }}
      >
        Note: Switch to table for more data
      </text> */}
        <CartesianGrid strokeDasharray="3 3" />

        <XAxis
          dataKey="name"
          interval={0}
          angle={-45}
          textAnchor="end"
          height={60}
          axisLine={{ stroke: "#000" }}
          tickLine={{ stroke: "#000" }}
          tick={{ fill: "#000" }}
        />

        <YAxis
          axisLine={{ stroke: "#000" }}
          tickLine={{ stroke: "#000" }}
          tick={{ fill: "#000" }}
        >
          <Label
            value="Value (%)"
            angle={-90}
            position="insideLeft"
            offset={-10}
            style={{ fill: "#000", fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif` }}
          />
        </YAxis>

        <Tooltip />

        <Legend verticalAlign="top" height={36} />
        <Bar
          dataKey="efficiency"
          name="Efficiency (%)"
          fill={colors.efficiency}
          onClick={(data, index) => handleBarClick(data, index)} // Add click handler
          shape={(props) => {
            const { x, y, width, height, fill } = props;
            const isSelected = selectedBar === props.index; // Check if bar is selected
            return (
              <rect
                x={x}
                y={y}
                width={width}
                height={height}
                fill={isSelected ? "#000" : fill} // Highlight selected bar
                stroke={isSelected ? "#fff" : "none"} // Add stroke for selected bar
                strokeWidth={isSelected ? 2 : 0}
                style={{
                  cursor: "pointer",
                  fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                }}
              />
            );
          }}
        />
        <Bar
          dataKey="durabilityPercent"
          name="TPI (%)"
          fill={colors.durabilityPercent}
          onClick={(data, index) => handleBarClick(data, index)} // Add click handler
          shape={(props) => {
            const { x, y, width, height, fill } = props;
            const isSelected = selectedBar === props.index; // Check if bar is selected
            return (
              <rect
                x={x}
                y={y}
                width={width}
                height={height}
                fill={isSelected ? "#000" : fill} // Highlight selected bar
                stroke={isSelected ? "#fff" : "none"} // Add stroke for selected bar
                strokeWidth={isSelected ? 2 : 0}
                style={{
                  cursor: "pointer",
                  fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                }}
              />
            );
          }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}
